import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Drawer, Group, Stack, Switch, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import type { BranchDto } from './types';
import { useCreateBranch, useUpdateBranch } from './branchesApi';

interface Props {
  opened: boolean;
  onClose: () => void;
  /** When present we edit; when null we create. */
  branch: BranchDto | null;
}

interface FormValues {
  code: string;
  nameAr: string;
  nameEn: string;
  isActive: boolean;
}

export function BranchFormDrawer({ opened, onClose, branch }: Props) {
  const { t, i18n } = useTranslation();
  const create = useCreateBranch();
  const update = useUpdateBranch();
  const submitting = create.isPending || update.isPending;
  const editing = branch !== null;
  // Drawer doesn't honour logical positions; flip per direction so it
  // always slides in from the inline-end edge.
  const drawerPosition = i18n.language === 'ar' ? 'left' : 'right';

  const form = useForm<FormValues>({
    initialValues: { code: '', nameAr: '', nameEn: '', isActive: true },
    validate: {
      code: (v) => (v.trim() ? null : t('common.errors.required')),
      nameAr: (v) => (v.trim() ? null : t('common.errors.required')),
    },
  });

  // Reset the form whenever we open in a different mode.
  useEffect(() => {
    if (!opened) return;
    form.setValues({
      code: branch?.code ?? '',
      nameAr: branch?.nameAr ?? '',
      nameEn: branch?.nameEn ?? '',
      isActive: branch?.isActive ?? true,
    });
    form.resetDirty();
    // We intentionally don't include `form` in deps — that'd loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, branch]);

  const submit = async (values: FormValues) => {
    const payload = {
      code: values.code.trim(),
      nameAr: values.nameAr.trim(),
      nameEn: values.nameEn.trim() || null,
    };
    try {
      if (editing) {
        await update.mutateAsync({
          id: branch.branchId,
          request: { ...payload, isActive: values.isActive },
        });
      } else {
        await create.mutateAsync(payload);
      }
      notifications.show({
        color: 'green',
        title: editing ? t('common.toasts.updated') : t('common.toasts.created'),
        message: payload.nameAr,
      });
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={editing ? t('branches.form.editTitle') : t('branches.form.createTitle')}
      position={drawerPosition}
      size="md"
      padding="lg"
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack gap="md">
          <TextInput
            label={t('branches.form.codeLabel')}
            description={t('branches.form.codeHelp')}
            placeholder="MAIN"
            withAsterisk
            {...form.getInputProps('code')}
          />
          <TextInput
            label={t('branches.form.nameArLabel')}
            placeholder={t('branches.form.nameArPlaceholder')}
            withAsterisk
            {...form.getInputProps('nameAr')}
          />
          <TextInput
            label={t('branches.form.nameEnLabel')}
            placeholder={t('branches.form.nameEnPlaceholder')}
            {...form.getInputProps('nameEn')}
          />
          {editing && (
            <Switch
              label={t('branches.form.activeLabel')}
              description={t('branches.form.activeHelp')}
              checked={form.values.isActive}
              onChange={(event) => form.setFieldValue('isActive', event.currentTarget.checked)}
            />
          )}

          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="default" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
